/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../services/Axios/axios";

const RESEND_COOLDOWN = 60; // seconds

function DeviceOtpVerification({ userId, maskedEmail, maskedPhone, onSuccess, onCancel }) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const updated = [...otp];
    updated[index] = value.slice(-1); // only last char
    setOtp(updated);
    setError("");

    // Auto-advance
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const updated = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) updated[i] = pasted[i];
    setOtp(updated);
    const nextEmpty = Math.min(pasted.length, 5);
    inputRefs.current[nextEmpty]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter the full 6-digit code.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.post("/auth/verify-device-otp", {
        user_id: userId,
        otp_code: Number(code),
      });
      onSuccess(response.data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Invalid or expired code. Please try again."
      );
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || resending) return;
    setResending(true);
    setError("");
    try {
      await axiosInstance.post("/auth/resend-device-otp", { user_id: userId });
      setOtp(["", "", "", "", "", ""]);
      setCountdown(RESEND_COOLDOWN);
      setCanResend(false);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to resend code. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full lg:w-1/2 p-8 md:p-12">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-7 h-7 text-green-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            New Device Detected
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            We sent a 6-digit verification code to
            {maskedEmail && (
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {" "}{maskedEmail}
              </span>
            )}
            {maskedPhone && maskedEmail && " and "}
            {maskedPhone && (
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {maskedPhone}
              </span>
            )}
            .
          </p>
          <p className="text-xs text-gray-500 mt-1">Code expires in 10 minutes.</p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          {/* OTP boxes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Enter Verification Code
            </label>
            <div className="flex gap-2 justify-between" onPaste={handlePaste}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white transition-colors focus:outline-none focus:border-green-600 ${
                    error
                      ? "border-red-400"
                      : digit
                      ? "border-green-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
              ))}
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>

          {/* Verify button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-800 text-white py-3 rounded-full font-medium hover:bg-green-700 transition duration-200 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>

          {/* Resend */}
          <div className="text-center">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-sm font-semibold text-green-700 dark:text-green-400 hover:underline disabled:opacity-60"
              >
                {resending ? "Sending..." : "Resend Code"}
              </button>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resend code in{" "}
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {countdown}s
                </span>
              </p>
            )}
          </div>

          {/* Back to login */}
          <div className="text-center">
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              ← Back to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeviceOtpVerification;
